/**
 * Componente Sonner - Sistema de notificações toast com design premium
 * 
 * Uso:
 * import { toast } from "sonner";
 * 
 * toast.success("Operação realizada com sucesso!");
 * toast.error("Ocorreu um erro ao processar");
 * toast.warning("Atenção: ação irreversível");
 * toast.info("Informação importante");
 * toast("Mensagem padrão");
 *
 * @version 2.0.0
 * RELEASE: 2026-08-21 v2.0.0 - Balões passam a aparecer SEMPRE no rodapé à
 *   direita (bottom-right), em todo o sistema. Antes ficavam no topo à direita
 *   e sobrepunham botões do cabeçalho (fechar modal, ações da página).
 *   Removido o detector de rota que existia apenas para excepcionar o editor
 *   de fluxos, já que agora a posição é única e global.
 */

import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, Info } from "lucide-react";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Provedor global de toasts com estilização premium
 * Deve ser incluído uma única vez no App.tsx
 *
 * Posição: bottom-right (rodapé à direita) em todas as telas — os balões
 * empilham de baixo para cima e não cobrem os botões do topo.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position="bottom-right"

      expand={false}

      richColors
      closeButton
      duration={4000}
      gap={12}
      icons={{
        success: <CheckCircle2 className="h-5 w-5" />,
        error: <XCircle className="h-5 w-5" />,
        warning: <AlertTriangle className="h-5 w-5" />,
        info: <Info className="h-5 w-5" />,
      }}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg group-[.toaster]:rounded-xl group-[.toaster]:px-4 group-[.toaster]:py-3",
          title: "group-[.toast]:font-semibold group-[.toast]:text-sm",
          description: "group-[.toast]:text-muted-foreground group-[.toast]:text-sm",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:rounded-md group-[.toast]:px-3 group-[.toast]:py-1.5 group-[.toast]:text-xs group-[.toast]:font-medium",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:rounded-md group-[.toast]:px-3 group-[.toast]:py-1.5 group-[.toast]:text-xs",
          closeButton: "group-[.toast]:bg-background group-[.toast]:border-border group-[.toast]:text-muted-foreground hover:group-[.toast]:text-foreground",
          // Estilos para tipos semânticos com richColors
          success: "group-[.toaster]:!bg-emerald-50 group-[.toaster]:!text-emerald-900 group-[.toaster]:!border-emerald-200 dark:group-[.toaster]:!bg-emerald-950 dark:group-[.toaster]:!text-emerald-100 dark:group-[.toaster]:!border-emerald-800",
          error: "group-[.toaster]:!bg-rose-50 group-[.toaster]:!text-rose-900 group-[.toaster]:!border-rose-200 dark:group-[.toaster]:!bg-rose-950 dark:group-[.toaster]:!text-rose-100 dark:group-[.toaster]:!border-rose-800",
          warning: "group-[.toaster]:!bg-amber-50 group-[.toaster]:!text-amber-900 group-[.toaster]:!border-amber-200 dark:group-[.toaster]:!bg-amber-950 dark:group-[.toaster]:!text-amber-100 dark:group-[.toaster]:!border-amber-800",
          info: "group-[.toaster]:!bg-sky-50 group-[.toaster]:!text-sky-900 group-[.toaster]:!border-sky-200 dark:group-[.toaster]:!bg-sky-950 dark:group-[.toaster]:!text-sky-100 dark:group-[.toaster]:!border-sky-800",
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
